import { useEffect, useState, useCallback } from "react";
import api from "./api";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// ---- Module-level cache so multiple components share a single fetch ----
let _cache = null;
let _inflight = null;
const _subscribers = new Set();

const _notify = (data) => {
  _subscribers.forEach((cb) => {
    try {
      cb(data);
    } catch {}
  });
};

const _fetchOnce = async () => {
  if (_inflight) return _inflight;
  _inflight = api
    .get("/site-content")
    .then((res) => {
      _cache = res.data || {};
      _notify(_cache);
      return _cache;
    })
    .catch(() => {
      _cache = {};
      _notify(_cache);
      return _cache;
    })
    .finally(() => {
      _inflight = null;
    });
  return _inflight;
};

/** Resolve a stored CMS value into a usable URL.
 * - Empty / null -> fallback
 * - "/api/media/..."  -> prefixed with REACT_APP_BACKEND_URL
 * - http(s)://... or relative /photos/... -> returned as-is
 */
export const resolveAsset = (value, fallback = "") => {
  const v = (value ?? "").trim();
  if (!v) return fallback;
  if (v.startsWith("/api/")) return `${BACKEND}${v}`;
  return v;
};

/** Read all CMS overrides as a `{key: value}` map. */
export function useSiteContentMap() {
  const [data, setData] = useState(_cache || {});
  const [ready, setReady] = useState(_cache !== null);

  useEffect(() => {
    const cb = (d) => {
      setData(d);
      setReady(true);
    };
    _subscribers.add(cb);
    if (_cache !== null) {
      cb(_cache);
    } else {
      _fetchOnce();
    }
    return () => {
      _subscribers.delete(cb);
    };
  }, []);

  const refresh = useCallback(async () => {
    _cache = null;
    const fresh = await _fetchOnce();
    return fresh;
  }, []);

  return { data, ready, refresh };
}

/** Read a single text value with a fallback. */
export function useSiteText(key, fallback = "") {
  const { data } = useSiteContentMap();
  const v = data?.[key];
  return v && v.trim() ? v : fallback;
}

/** Read an image url (resolved) with a fallback. */
export function useSiteImage(key, fallback = "") {
  const { data } = useSiteContentMap();
  return resolveAsset(data?.[key], fallback);
}

/** Force-bust the cache (e.g. after admin save). */
export const refreshSiteContent = () => {
  _cache = null;
  return _fetchOnce();
};
